<template>
  <div class="empresa-detalle">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Detalle de Empresa</h1>
        <p class="content-header-subtitle">Información completa de la empresa</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver" class="mr-2">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
        <b-button variant="primary" @click="editarEmpresa">
          <feather-icon icon="EditIcon" size="16" class="mr-50" />
          Editar
        </b-button>
      </div>
    </div>

    <!-- Indicador de carga -->
    <div v-if="cargando" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando empresa..."></b-spinner>
      <p class="mt-2 text-muted">Cargando información de la empresa...</p>
    </div>

    <!-- Error al cargar -->
    <div v-else-if="error" class="text-center py-4">
      <b-alert variant="danger" show>
        <h4 class="alert-heading">Error al cargar la empresa</h4>
        <p>{{ error }}</p>
        <hr>
        <b-button variant="outline-danger" @click="cargarEmpresa">
          <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
          Reintentar
        </b-button>
      </b-alert>
    </div>

    <!-- Contenido de la empresa -->
    <div v-else-if="empresa" class="row">
      <!-- Información general -->
      <div class="col-md-8">
        <b-card title="Información General" class="mb-4">
          <b-row>
            <b-col md="6">
              <b-form-group label="Nombre de la Empresa">
                <div class="d-flex align-items-center">
                  <b-avatar
                    :text="empresa.nombre.charAt(0).toUpperCase()"
                    variant="primary"
                    class="mr-2"
                    size="lg"
                  />
                  <div>
                    <h5 class="mb-0">{{ empresa.nombre }}</h5>
                    <small class="text-muted">CIF: {{ empresa.cif }}</small>
                  </div>
                </div>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Tipo de Empresa">
                <b-badge variant="info" class="p-2">
                  {{ empresa.nombreTipoEmpresa || 'Sin tipo' }}
                </b-badge>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-form-group label="Dirección">
            <p class="form-control-plaintext">
              <feather-icon icon="MapPinIcon" size="16" class="mr-1" />
              {{ empresa.direccion }}
            </p>
          </b-form-group>

          <b-form-group label="Observaciones" v-if="empresa.observaciones">
            <p class="form-control-plaintext">{{ empresa.observaciones }}</p>
          </b-form-group>
        </b-card>

        <!-- Información de contacto -->
        <b-card title="Información de Contacto" class="mb-4">
          <b-row>
            <b-col md="6">
              <b-form-group label="Email" v-if="empresa.email">
                <p class="form-control-plaintext">
                  <feather-icon icon="MailIcon" size="16" class="mr-1" />
                  <a href="#" @click.prevent="enviarEmail" class="text-decoration-none text-primary" style="cursor: pointer;">
                    {{ empresa.email }}
                  </a>
                </p>
              </b-form-group>
              <b-form-group v-else label="Email">
                <p class="form-control-plaintext text-muted">
                  <feather-icon icon="MailIcon" size="16" class="mr-1" />
                  Sin email registrado
                </p>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Teléfono" v-if="empresa.telefono">
                <p class="form-control-plaintext">
                  <feather-icon icon="PhoneIcon" size="16" class="mr-1" />
                  <a :href="`tel:${empresa.telefono}`" class="text-decoration-none">
                    {{ empresa.telefono }}
                  </a>
                </p>
              </b-form-group>
              <b-form-group v-else label="Teléfono">
                <p class="form-control-plaintext text-muted">
                  <feather-icon icon="PhoneIcon" size="16" class="mr-1" />
                  Sin teléfono registrado
                </p>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Página Web" v-if="empresa.web">
            <p class="form-control-plaintext">
              <feather-icon icon="GlobeIcon" size="16" class="mr-1" />
              <a :href="empresa.web" target="_blank" class="text-decoration-none">
                {{ empresa.web }}
                <feather-icon icon="ExternalLinkIcon" size="14" class="ml-1" />
              </a>
            </p>
          </b-form-group>
          <b-form-group v-else label="Página Web">
            <p class="form-control-plaintext text-muted">
              <feather-icon icon="GlobeIcon" size="16" class="mr-1" />
              Sin página web registrada
            </p>
          </b-form-group>
        </b-card>

        <!-- Contactos de la empresa -->
        <b-card title="Contactos de la Empresa" class="mb-4">
          <GestionContactos 
            :id-empresa="empresa.idEmpresa"
            @contacto-creado="onContactoCreado"
            @contacto-actualizado="onContactoActualizado"
            @contacto-eliminado="onContactoEliminado"
            @enviar-email-contacto="enviarEmailContacto"
          />
        </b-card>

        <!-- Obras relacionadas -->
        <b-card title="Obras Relacionadas" class="mb-4" v-if="obrasRelacionadas.length > 0">
          <b-table
            :items="obrasRelacionadas"
            :fields="obrasFields"
            responsive
            striped
            hover
            small
          >
            <template #cell(direccion)="data">
              <div class="text-truncate" style="max-width: 200px;">
                {{ data.item.direccion }}
              </div>
            </template>
            <template #cell(estado)="data">
              <b-badge
                :variant="getEstadoObraVariant(data.item.nombreEstadoObra)"
                class="text-capitalize"
              >
                {{ data.item.nombreEstadoObra }}
              </b-badge>
            </template>
            <template #cell(acciones)="data">
              <b-button
                variant="outline-primary"
                size="sm"
                @click="verObra(data.item)"
              >
                <feather-icon icon="EyeIcon" size="14" />
              </b-button>
            </template>
          </b-table>
        </b-card>
      </div>

      <!-- Panel lateral -->
      <div class="col-md-4">
        <!-- Información adicional -->
        <b-card title="Información Adicional" class="mb-4">
          <b-list-group flush>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>ID de Empresa</span>
              <b-badge variant="secondary">{{ empresa.idEmpresa }}</b-badge>
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-center">
              <span>Obras Asociadas</span>
              <b-badge variant="info">{{ obrasRelacionadas.length }}</b-badge>
            </b-list-group-item>
          </b-list-group>
        </b-card>

        <!-- Acciones rápidas -->
        <b-card title="Acciones Rápidas">
          <div class="d-grid gap-2">
            <b-button variant="outline-primary" @click="editarEmpresa" block>
              <feather-icon icon="EditIcon" size="16" class="mr-1" />
              Editar Empresa
            </b-button>
            <b-button variant="outline-success" @click="nuevaObra" block>
              <feather-icon icon="PlusIcon" size="16" class="mr-1" />
              Nueva Obra
            </b-button>
          </div>
        </b-card>
      </div>
    </div>

    <!-- Modal Enviar Correo -->
    <b-modal
      id="modal-enviar-correo"
      ref="modalEnviarCorreo"
      title="Enviar Correo"
      size="lg"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      @show="onModalShow"
      @hidden="onModalHidden"
    >
      <div v-if="empresa || contactoSeleccionado">
        <b-alert variant="info" show class="mb-3">
          <div class="d-flex align-items-center">
            <feather-icon :icon="contactoSeleccionado ? 'UserIcon' : 'BriefcaseIcon'" size="16" class="mr-2" />
            <div>
              <strong>{{ contactoSeleccionado ? contactoSeleccionado.nombre : empresa.nombre }}</strong>
              <br>
              <small>{{ contactoSeleccionado ? contactoSeleccionado.email : empresa.email }}</small>
            </div>
          </div>
        </b-alert>

        <b-form @submit.prevent="procesarEnvio">
          <!-- Redacción libre cuando no hay plantilla seleccionada -->
          <b-card v-if="!plantillaSeleccionada" class="mb-2" body-class="pt-1">
            <b-alert variant="secondary" show class="py-1 mb-2">
              Redacción libre (sin plantilla)
            </b-alert>
            <b-form-group label="Asunto" label-for="asunto-libre">
              <b-form-input id="asunto-libre" v-model="asuntoLibre" placeholder="Asunto del correo" />
            </b-form-group>
            <b-form-group label="Contenido">
              <quill-editor v-model="contenidoLibre" :options="editorOptions" style="height: 220px;" />
            </b-form-group>
            <b-form-group label="Adjuntos (opcional)">
              <input
                type="file"
                multiple
                class="form-control"
                @change="onFilesSelectedLibre"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.csv,.zip"
              />
              <div v-if="adjuntosLibres.length" class="mt-2">
                <small class="text-muted">{{ adjuntosLibres.length }} archivo(s) seleccionados</small>
                <ul class="mb-0 mt-1">
                  <li v-for="(f, i) in adjuntosLibres" :key="i">{{ f.name }}</li>
                </ul>
                <b-button size="sm" variant="outline-danger" class="mt-1" @click="limpiarAdjuntosLibre">Limpiar</b-button>
              </div>
            </b-form-group>
          </b-card>

          <b-tabs content-class="mt-2">
            <!-- Tab de Plantilla -->
            <b-tab title="Plantilla" active>
              <b-form-group label="Seleccionar Plantilla">
                <v-select
                  v-model="plantillaSeleccionada"
                  :options="plantillasActivas"
                  label="nombre"
                  placeholder="Seleccionar plantilla..."
                  @input="onPlantillaSeleccionada"
                />
              </b-form-group>
            </b-tab>

            <!-- Tab de Variables -->
            <b-tab title="Variables" :disabled="!plantillaSeleccionada">
              <div v-if="variablesPlantilla.length > 0">
                <b-form-group
                  v-for="v in variablesPlantilla"
                  :key="v.id"
                  :label="v.label || v.key"
                  :label-for="`var_${v.id}`"
                >
                  <b-form-input
                    :id="`var_${v.id}`"
                    v-model="valoresVariables[v.key]"
                    :placeholder="`Valor para ${v.label || v.key}`"
                  />
                </b-form-group>
              </div>
              <b-alert v-else variant="info" show>
                Esta plantilla no tiene variables personalizables.
              </b-alert>
            </b-tab>

            <!-- Tab de Adjuntos -->
            <b-tab title="Adjuntos" :disabled="!plantillaSeleccionada">
              <b-form-group label="Archivos Adjuntos">
                <input
                  ref="fileInput"
                  type="file"
                  multiple
                  class="form-control"
                  @change="onFilesSelected"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                />
                <small class="form-text text-muted">
                  Puedes seleccionar múltiples archivos. Formatos permitidos: PDF, DOC, DOCX, XLS, XLSX, JPG, JPEG, PNG
                </small>
              </b-form-group>

              <div v-if="archivosAdjuntos.length > 0" class="mt-3">
                <h6>Archivos seleccionados:</h6>
                <div class="d-flex flex-wrap gap-2">
                  <b-badge
                    v-for="(archivo, index) in archivosAdjuntos"
                    :key="index"
                    variant="secondary"
                    class="d-flex align-items-center"
                  >
                    {{ archivo.name }}
                    <feather-icon
                      icon="XIcon"
                      size="12"
                      class="ml-1 cursor-pointer"
                      @click="eliminarArchivo(index)"
                    />
                  </b-badge>
                </div>
                <b-button
                  variant="outline-danger"
                  size="sm"
                  class="mt-2"
                  @click="limpiarArchivos"
                >
                  Limpiar todos
                </b-button>
              </div>
            </b-tab>

            <!-- Tab de Vista Previa -->
            <b-tab title="Vista Previa" :disabled="!plantillaSeleccionada">
              <div v-if="plantillaSeleccionada">
                <h6>Asunto:</h6>
                <p class="border p-2 rounded">{{ asuntoProcesado }}</p>
                <h6>Contenido:</h6>
                <div class="border p-2 rounded" v-html="contenidoProcesado"></div>
              </div>
            </b-tab>
          </b-tabs>
        </b-form>
      </div>

      <template #modal-footer>
        <b-button variant="secondary" @click="cerrarModal">
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          @click="procesarEnvio"
          :disabled="enviando || (!plantillaSeleccionada && (!asuntoLibre || !contenidoLibre))"
        >
          <b-spinner v-if="enviando" small class="mr-1" />
          {{ enviando ? 'Enviando...' : 'Enviar Correo' }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { EmpresaService } from '@/services/empresaService'
import { ObraService } from '@/services/obraService'
import { Empresa, Obra } from '@/types'
import GestionContactos from '@/components/GestionContactos.vue'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import vSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import plantillaCorreoService from '@/services/plantillaCorreoService'

export default {
  name: 'EmpresaDetalle',
  components: {
    GestionContactos,
    quillEditor,
    vSelect
  },
  data() {
    return {
      empresa: null,
      obrasRelacionadas: [],
      cargando: false,
      error: null,
      obrasFields: [
        { key: 'direccion', label: 'Dirección', sortable: true },
        { key: 'municipio', label: 'Municipio', sortable: true },
        { key: 'estado', label: 'Estado', sortable: true },
        { key: 'fechaInicioObra', label: 'Fecha Inicio', sortable: true },
        { key: 'acciones', label: 'Acciones', thClass: 'text-center' }
      ],
      // modal correo
      plantillasActivas: [],
      plantillaSeleccionada: null,
      variablesPlantilla: [],
      valoresVariables: {},
      enviando: false,
      asuntoLibre: '',
      contenidoLibre: '',
      adjuntosLibres: [],
      archivosAdjuntos: [],
      contactoSeleccionado: null,
      editorOptions: {
        theme: 'snow',
        placeholder: 'Escribe el contenido del correo aquí...',
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['clean'],
            ['link']
          ]
        }
      },
    }
  },
  computed: {
    asuntoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      return this.procesarTexto(this.plantillaSeleccionada.asunto, this.valoresVariables)
    },
    contenidoProcesado() {
      if (!this.plantillaSeleccionada) return ''
      return this.procesarTexto(this.plantillaSeleccionada.contenidoHtml, this.valoresVariables)
    }
  },
  async mounted() {
    await this.cargarEmpresa()
  },
  methods: {
    async cargarEmpresa() {
      this.cargando = true
      this.error = null
      
      try {
        const empresaId = this.$route.params.id
        this.empresa = await EmpresaService.getById(empresaId)
        await this.cargarObrasRelacionadas()
      } catch (error) {
        this.error = 'No se pudo cargar la información de la empresa'
        console.error('Error al cargar empresa:', error)
      } finally {
        this.cargando = false
      }
    },
    async cargarObrasRelacionadas() {
      try {
        if (this.empresa?.idEmpresa) {
          this.obrasRelacionadas = await ObraService.getByEmpresa(this.empresa.idEmpresa)
        } else {
          this.obrasRelacionadas = []
        }
      } catch (error) {
        console.error('Error al cargar obras relacionadas:', error)
        this.obrasRelacionadas = []
      }
    },
    volver() {
      this.$router.push({ name: 'empresas-lista' })
    },
    editarEmpresa() {
      this.$router.push({ 
        name: 'empresas-editar', 
        params: { id: this.empresa.idEmpresa } 
      })
    },
    verObra(obra) {
      this.$router.push({ 
        name: 'obras-detalle', 
        params: { id: obra.idObra } 
      })
    },
    nuevaObra() {
      this.$router.push({ 
        name: 'obras-nueva',
        query: { empresa: this.empresa.idEmpresa }
      })
    },
    formatearFecha(fecha) {
      if (!fecha) return 'No disponible'
      return new Date(fecha).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    getEstadoObraVariant(estado) {
      const estados = {
        'En Progreso': 'primary',
        'Completada': 'success',
        'Pausada': 'warning',
        'Cancelada': 'danger',
        'Planificada': 'info'
      }
      return estados[estado] || 'secondary'
    },
    onContactoCreado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} agregado correctamente`)
    },
    onContactoActualizado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} actualizado correctamente`)
    },
    onContactoEliminado(contacto) {
      this.$toast.success(`Contacto ${contacto.nombre} eliminado correctamente`)
    },
    async enviarEmail() {
      if (!this.empresa?.email) return
      this.contactoSeleccionado = null
      await this.cargarPlantillasActivas()
      this.$refs.modalEnviarCorreo.show()
    },
    async enviarEmailContacto(contacto) {
      if (!contacto?.email) return
      this.contactoSeleccionado = contacto
      await this.cargarPlantillasActivas()
      this.$refs.modalEnviarCorreo.show()
    },
    onModalShow() {
      // Resetear estado
      this.plantillaSeleccionada = null
      this.variablesPlantilla = []
      this.valoresVariables = {}
      this.asuntoLibre = ''
      this.contenidoLibre = ''
      this.adjuntosLibres = []
      this.archivosAdjuntos = []
    },
    onModalHidden() {
      // Limpieza adicional si fuese necesario
      this.enviando = false
      this.archivosAdjuntos = []
      this.contactoSeleccionado = null
    },
    onFilesSelected(event) {
      const files = Array.from(event.target.files)
      this.archivosAdjuntos = [...this.archivosAdjuntos, ...files]
    },
    eliminarArchivo(index) {
      this.archivosAdjuntos.splice(index, 1)
    },
    limpiarArchivos() {
      this.archivosAdjuntos = []
      this.$refs.fileInput && (this.$refs.fileInput.value = '')
    },
    async cargarPlantillasActivas() {
      try {
        const plantillas = await plantillaCorreoService.getAllPlantillas()
        this.plantillasActivas = plantillas.filter(p => p.activa)
      } catch (e) {
        console.error('Error al cargar plantillas:', e)
        this.plantillasActivas = []
      }
    },
    onPlantillaSeleccionada(plantilla) {
      this.variablesPlantilla = []
      this.valoresVariables = {}
      if (!plantilla) return

      // Preferir objetos de variables si existen
      if (Array.isArray(plantilla.variables) && plantilla.variables.length > 0) {
        if (typeof plantilla.variables[0] === 'string') {
          const normalizadas = plantilla.variables
            .map(v => (typeof v === 'string' ? v.trim() : ''))
            .filter(v => v && v.length > 0)
          this.variablesPlantilla = normalizadas.map((nombre, idx) => ({
            id: `${nombre}_${idx}`,
            key: nombre,
            label: nombre,
          }))
          normalizadas.forEach(nombre => { this.valoresVariables[nombre] = '' })
        } else {
          this.variablesPlantilla = (plantilla.variables || [])
            .map((obj, idx) => {
              const key = obj?.nombreVariable || obj?.nombre
              return key ? ({ id: `${key}_${idx}`, key, label: key }) : null
            })
            .filter(Boolean)
          this.variablesPlantilla.forEach(v => { this.valoresVariables[v.key] = '' })
        }
        return
      }

      // Si no, parsear variablesDisponibles (JSON)
      if (typeof plantilla.variablesDisponibles === 'string' && plantilla.variablesDisponibles.trim().length > 0) {
        try {
          const vars = JSON.parse(plantilla.variablesDisponibles)
          if (Array.isArray(vars)) {
            const normalizadas = vars
              .map(v => (typeof v === 'string' ? v.trim() : ''))
              .filter(v => v && v.length > 0)
            this.variablesPlantilla = normalizadas.map((nombre, idx) => ({
              id: `${nombre}_${idx}`,
              key: nombre,
              label: nombre,
            }))
            normalizadas.forEach(nombre => { this.valoresVariables[nombre] = '' })
          }
        } catch (e) {
          console.warn('No se pudo parsear variablesDisponibles:', e)
          this.variablesPlantilla = []
        }
      }
    },
    procesarTexto(texto, variables) {
      if (!texto || !variables) return texto
      let resultado = texto
      Object.keys(variables).forEach(key => {
        const valor = variables[key] || ''
        const regex = new RegExp(`\\{\\{${key}\\}\\}`, 'g')
        resultado = resultado.replace(regex, valor)
      })
      return resultado
    },
    async procesarEnvio() {
      const emailDestino = this.contactoSeleccionado?.email || this.empresa?.email
      if (!emailDestino) return
      try {
        this.enviando = true
        if (this.plantillaSeleccionada) {
          const formData = new FormData()
          formData.append('idPlantilla', this.plantillaSeleccionada.idPlantilla)
          formData.append('valoresVariables', JSON.stringify(this.valoresVariables))
          formData.append('destinatarios', JSON.stringify([emailDestino]))
          await plantillaCorreoService.procesarPlantillaConAdjuntos(formData)
        } else {
          if (this.adjuntosLibres.length > 0) {
            const formData = new FormData()
            formData.append('asunto', this.asuntoLibre || '')
            formData.append('contenidoHtml', this.contenidoLibre || '')
            formData.append('destinatarios', JSON.stringify([emailDestino]))
            this.adjuntosLibres.forEach(f => formData.append('adjuntos', f))
            await plantillaCorreoService.enviarCorreoLibreConAdjuntos(formData)
          } else {
            await plantillaCorreoService.enviarCorreoLibre({
              asunto: this.asuntoLibre || '',
              contenidoHtml: this.contenidoLibre || '',
              destinatarios: [emailDestino],
            })
          }
        }
        this.$toast && this.$toast.success && this.$toast.success('Correo enviado correctamente')
        this.$refs.modalEnviarCorreo.hide()
      } catch (e) {
        this.$toast && this.$toast.error && this.$toast.error('Error al enviar el correo')
        console.error('Error al enviar correo:', e)
      } finally {
        this.enviando = false
      }
    },
    onFilesSelectedLibre(e) {
      const files = Array.from(e.target.files || [])
      this.adjuntosLibres = files
    },
    limpiarAdjuntosLibre() {
      this.adjuntosLibres = []
    },
    cerrarModal() {
      this.$refs.modalEnviarCorreo.hide()
    }
  }
}
</script>

<style scoped>
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
}

.content-header-left h1 {
  margin-bottom: 0.5rem;
  font-size: 1.75rem;
  font-weight: 600;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.content-header-right {
  display: flex;
  gap: 0.5rem;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.form-control-plaintext {
  padding: 0.375rem 0;
  margin-bottom: 0;
  line-height: 1.5;
  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0;
}

.list-group-item {
  border-left: none;
  border-right: none;
}

.list-group-item:first-child {
  border-top: none;
}

.list-group-item:last-child {
  border-bottom: none;
}
</style>






